const HERO_BANNER_DRAG_THRESHOLD_PX = 40;
const HERO_BANNER_TEXT_MIN = 8;

class HeroBannerSection extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-track]");
    this.viewport = this.querySelector("[data-track-viewport]");
    this.items = Array.from(this.querySelectorAll("[data-item]"));
    this.prevBtn = this.querySelector("[data-prev]");
    this.nextBtn = this.querySelector("[data-next]");

    this.visibleMobile = Math.max(1, Number(this.dataset.visibleMobile) || 3);
    this.currentIndex = 0;
    this._drag = null;
    this._fitRaf = null;
    this._abort = new AbortController();

    this.bindControls();
    this.updateCarousel(false);
    this.scheduleTextFit();

    const { signal } = this._abort;
    window.addEventListener("resize", () => {
      this.updateCarousel(false);
      this.scheduleTextFit();
    }, { signal });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.scheduleTextFit());
    }
  }

  disconnectedCallback() {
    this._abort.abort();
  }

  isMobile() {
    return window.matchMedia("(min-width: 990px)").matches === false;
  }

  getMaxIndex() {
    if (!this.isMobile() || this.items.length <= this.visibleMobile) return 0;
    return this.items.length - this.visibleMobile;
  }

  readCssPx(varName, fallback) {
    const raw = getComputedStyle(this).getPropertyValue(varName).trim();
    const value = parseFloat(raw);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  getTextMax() {
    return this.isMobile()
      ? this.readCssPx("--hero-banner-text-max-m", 12)
      : this.readCssPx("--hero-banner-text-max-d", 14);
  }

  getTextFitVar() {
    return this.isMobile() ? "--hero-banner-text-fit-m" : "--hero-banner-text-fit-d";
  }

  setUniformTextSize(size) {
    this.style.setProperty(this.getTextFitVar(), size + "px");
  }

  clearUniformTextSize() {
    this.style.removeProperty(this.getTextFitVar());
  }

  allItemsFit(size) {
    this.setUniformTextSize(size);
    void this.offsetHeight;

    return this.items.every((item) => {
      const link = item.querySelector(".hero-banner__link");
      if (!link || item.clientWidth < 16) return true;
      return link.scrollWidth <= item.clientWidth;
    });
  }

  fitUniformText() {
    if (!this.items.length) return;

    const maxSize = Math.round(this.getTextMax());
    const minSize = HERO_BANNER_TEXT_MIN;

    this.clearUniformTextSize();
    void this.offsetHeight;

    for (let size = maxSize; size >= minSize; size -= 1) {
      if (this.allItemsFit(size)) {
        this.setUniformTextSize(size);
        return;
      }
    }

    this.setUniformTextSize(minSize);
  }

  scheduleTextFit() {
    if (this._fitRaf != null) return;
    this._fitRaf = requestAnimationFrame(() => {
      this._fitRaf = null;
      this.fitUniformText();
    });
  }

  bindControls() {
    const { signal } = this._abort;

    this.prevBtn?.addEventListener(
      "click",
      () => {
        this.goTo(this.currentIndex - 1);
      },
      { signal }
    );

    this.nextBtn?.addEventListener(
      "click",
      () => {
        this.goTo(this.currentIndex + 1);
      },
      { signal }
    );

    this.bindSwipe(signal);
  }

  bindSwipe(signal) {
    if (!this.track || !this.viewport || this.items.length <= this.visibleMobile) return;

    const onPointerDown = (event) => {
      if (!this.isMobile()) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (event.target.closest("a, button, input, label")) return;

      this._drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
      };
      this.viewport.setPointerCapture(event.pointerId);
    };

    const onPointerEnd = (event) => {
      if (!this._drag || event.pointerId !== this._drag.pointerId) return;

      const deltaX = event.clientX - this._drag.startX;

      try {
        this.viewport.releasePointerCapture(event.pointerId);
      } catch (_error) {
        /* already released */
      }

      this._drag = null;

      if (Math.abs(deltaX) >= HERO_BANNER_DRAG_THRESHOLD_PX) {
        if (deltaX < 0) this.goTo(this.currentIndex + 1);
        else this.goTo(this.currentIndex - 1);
      }
    };

    this.viewport.addEventListener("pointerdown", onPointerDown, { signal });
    this.viewport.addEventListener("pointerup", onPointerEnd, { signal });
    this.viewport.addEventListener("pointercancel", onPointerEnd, { signal });
  }

  canLoopMobile() {
    return this.isMobile() && this.getMaxIndex() > 0;
  }

  updateArrows() {
    if (!this.prevBtn && !this.nextBtn) return;

    if (this.canLoopMobile()) {
      if (this.prevBtn) this.prevBtn.disabled = false;
      if (this.nextBtn) this.nextBtn.disabled = false;
      return;
    }

    const maxIndex = this.getMaxIndex();
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex <= 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= maxIndex;
    }
  }

  goTo(index) {
    const maxIndex = this.getMaxIndex();

    if (this.canLoopMobile()) {
      if (index < 0) this.currentIndex = maxIndex;
      else if (index > maxIndex) this.currentIndex = 0;
      else this.currentIndex = index;
    } else {
      this.currentIndex = Math.min(Math.max(index, 0), maxIndex);
    }

    this.updateCarousel(true);
  }

  updateCarousel(animate) {
    if (!this.track || !this.items.length) return;

    if (!this.isMobile() || this.items.length <= this.visibleMobile) {
      this.track.style.transform = "";
      this.track.style.transition = "";
      this.currentIndex = 0;
      this.updateArrows();
      this.scheduleTextFit();
      return;
    }

    const item = this.items[0];
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const offset = (item.offsetWidth + gap) * this.currentIndex;

    this.track.style.transition = animate ? "transform 350ms ease" : "none";
    this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    this.updateArrows();
    this.scheduleTextFit();
  }
}

if (!customElements.get("hero-banner-section")) {
  customElements.define("hero-banner-section", HeroBannerSection);
}
